import React from "react";
import { Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { Coins, Home, Library, User, Moon, Sun, LogOut, Plus, Shield, GraduationCap } from "lucide-react";

const studentNav = [
  { to: "/app", label: "Home", icon: Home, testid: "nav-home" },
  { to: "/app/courses", label: "Courses", icon: Library, testid: "nav-courses" },
  { to: "/app/billing", label: "Tokens", icon: Coins, testid: "nav-billing" },
  { to: "/app/account", label: "Account", icon: User, testid: "nav-account" },
];
const adminNav = [{ to: "/app/admin", label: "Admin", icon: Shield, testid: "nav-admin" }];
const tutorNav = [{ to: "/app/tutor", label: "Tutor", icon: GraduationCap, testid: "nav-tutor" }];

export function AppLayout() {
  const { user, balance, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const role = user?.role;
  const navItems = role === "admin" ? adminNav : role === "tutor" ? tutorNav : studentNav;
  const isStudent = role === "student";

  const isActive = (to) => (to === "/app" ? location.pathname === "/app" : location.pathname.startsWith(to));

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <Link to="/app" className="shrink-0"><Logo /></Link>
          <nav className="hidden items-center gap-1 md:flex">
            {navItems.map((n) => (
              <Link key={n.to} to={n.to} data-testid={n.testid}
                className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-200 ${isActive(n.to) ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"}`}>
                <n.icon className="h-4 w-4" />{n.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            {isStudent && (
            <Link to="/app/billing" data-testid="token-balance-widget" className="flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-sm font-semibold transition-colors duration-200 hover:border-[#0B5D3B]/40">
              <Coins className="h-4 w-4 text-[#D97706]" />
              <span data-testid="token-balance-value">{balance}</span>
              <span className="hidden text-muted-foreground sm:inline">tokens</span>
              <Plus className="h-3.5 w-3.5 text-[#0B5D3B] dark:text-[#25D366]" />
            </Link>
            )}
            <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" aria-label="Toggle theme">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={async () => { await logout(); navigate("/"); }} data-testid="logout-btn" aria-label="Log out">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
        <Outlet />
      </main>

      {/* mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 flex items-center justify-around border-t border-border bg-background/95 py-2 backdrop-blur md:hidden">
        {navItems.map((n) => (
          <Link key={n.to} to={n.to} data-testid={`${n.testid}-mobile`}
            className={`flex flex-col items-center gap-0.5 px-4 py-1 text-xs ${isActive(n.to) ? "text-primary" : "text-muted-foreground"}`}>
            <n.icon className="h-5 w-5" />{n.label}
          </Link>
        ))}
      </nav>
    </div>
  );
}
